import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { Drawer, Form, Input } from "antd";
import { ColumnProps } from "antd/lib/table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormInstance } from "antd/lib/form";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { GroupSelect } from "Components/General/GroupSelect";
import { emailInboxListStore } from "../../Stores/EmailInboxListStore";
import { observer } from "mobx-react";
import { Button } from "Components/General/Button";
import { groupListStore } from "Pages/Project/Groups/Stores/GroupsStore";
import { modal } from "Components/General/Modal/AntdModal";
import { Table } from "Components/General/Table";

interface InboxesModalProps extends WithTranslation, ModalComponentProps {}

interface InboxesModalState {
  visible: boolean;
  editingId: number; // to know if creating or editing a record
}

export const InboxesModal = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<InboxesModalProps, InboxesModalState> {
      formRef: React.RefObject<FormInstance> = React.createRef();

      constructor(props: InboxesModalProps) {
        super(props);
        this.state = {
          visible: false,
          editingId: null,
        };
      }

      componentDidMount() {
        emailInboxListStore.fetch();
      }

      showDrawer = (record: API_MailInbox) => {
        this.setState({ visible: true, editingId: record.id }, () => {
          if (!record.groups) {
            record.groups = [];
          }
          this.formRef.current.setFieldsValue(record);
        });
        this.props.setConfirmOnCancel(true);
      };

      createInbox = () => {
        this.setState({ visible: true });
        this.props.setConfirmOnCancel(true);
      };

      onClose = () => {
        this.setState({ visible: false, editingId: null }, () => this.formRef.current.resetFields());
        this.props.setConfirmOnCancel(false);
      };

      confirmDelete = (inboxId) => {
        const { t } = this.props;
        const onOk = async () => {
          await emailInboxListStore.deleteItem(inboxId);
          notificationSuccess(t("Success"), t("Inbox deleted"));
        };
        modal.confirm({
          title: t("Delete inbox?"),
          onOk,
          icon: <ExclamationCircleOutlined />,
          okText: t("Yes"),
          okType: "default",
          cancelButtonProps: { type: "primary" },
          cancelText: t("No"),
          zIndex: 1052,
        });
      };

      handleFinish = async (values) => {
        const { t } = this.props;
        const { editingId } = this.state;
        try {
          if (editingId) {
            await emailInboxListStore.editItem(editingId, values);
            notificationSuccess(t("Success"), t("Inbox edited"));
          } else {
            await emailInboxListStore.createItem(values);
            notificationSuccess(t("Success"), t("Inbox created"));
          }
          this.onClose();
        } catch (error) {
          throw error;
        }
      };

      render() {
        const { t } = this.props;
        const columns: ColumnProps<API_MailInbox>[] = [
          {
            dataIndex: "name",
            title: t("Name"),
          },
          {
            dataIndex: "description",
            title: t("Description"),
          },
          {
            dataIndex: "groups",
            title: t("Groups"),
            render: (_, rec) => rec.groups?.map((groupId) => groupListStore.getItem(groupId)?.name).join(", ") || [],
          },
          {
            dataIndex: "id",
            title: t("Actions"),
            width: 110,
            render: (_, rec) => (
              <>
                <Button size="small" type="link" onClick={() => this.showDrawer(rec)}>
                  <FontAwesomeIcon icon={["far", "edit"]} fixedWidth={true} />
                </Button>
                {rec.deletable && (
                  <Button size="small" type="link" onClick={() => this.confirmDelete(rec.id)}>
                    <FontAwesomeIcon icon={["far", "trash-alt"]} fixedWidth={true} />
                  </Button>
                )}
              </>
            ),
          },
        ];

        return (
          <div style={{ display: "flex", height: "100%", flexDirection: "column" }}>
            <div className="text-right">
              <Button type="primary" style={{ marginBottom: 10 }} faIcon={["far", "plus"]} onClick={this.createInbox}>
                {t("New inbox")}
              </Button>
            </div>
            <Table
              name="mailInboxes"
              rowKey="id"
              columns={columns}
              loading={emailInboxListStore.state === "LOADING"}
              dataSource={emailInboxListStore.list}
              pagination={{ hideOnSinglePage: true }}
            />
            <Drawer
              destroyOnHidden={true}
              title={t("Edit inbox")}
              placement="right"
              closable={false}
              onClose={this.onClose}
              open={this.state.visible}
              getContainer={() => document.querySelector(".ant-modal-container")}
              style={{ position: "absolute" }}
              width={400}
              footer={
                <div className="text-right">
                  <Button
                    type="primary"
                    loading={emailInboxListStore.actionState === "LOADING"}
                    onClick={() => this.formRef.current.submit()}
                  >
                    {t("Save")}
                  </Button>
                </div>
              }
            >
              <Form ref={this.formRef} onFinish={this.handleFinish} layout="vertical">
                <Form.Item label={t("Name")} name="name" rules={[{ required: true, message: t("Name is required.") }]}>
                  <Input />
                </Form.Item>
                <Form.Item label={t("Description")} name="description">
                  <Input />
                </Form.Item>
                <Form.Item label={t("Group")} name="groups">
                  <GroupSelect mode="multiple" groupType="FOR_RIGHTS" />
                </Form.Item>
              </Form>
            </Drawer>
          </div>
        );
      }
    }
  )
);
